import { SelectOption } from '@kanda-libs/ks-component-ts';
import { FinanceRate } from '@kanda-libs/ks-frontend-services';

const getRatesForApr = (apr: string, rates: FinanceRate[] = []) =>
  rates.filter((rate) => rate.apr === parseInt(apr, 10));

export const getDurationsForApr = (apr: string, rates: FinanceRate[] = []) => {
  const filteredRates = getRatesForApr(apr, rates);
  const durations = filteredRates.map((rate) => rate.duration);

  const deDuplicatedDurations = durations
    .map((duration, index) => durations.indexOf(duration) === index && duration)
    .filter(Boolean);

  return deDuplicatedDurations.sort((a, b) => (a || 0) - (b || 0)) as number[];
};

const getYearsLabel = (months: number) => {
  const years = months / 12;
  const suffix = years === 1 ? 'year' : 'years';

  if (years < 2) {
    return '';
  }

  return `( ${years} ${suffix} )`;
};

export const mapDurationsToSelectOptions = (
  durations: number[],
): SelectOption[] => {
  const options = durations.map((duration) => ({
    value: duration.toString(),
    name: `${duration} months ${getYearsLabel(duration)}`,
  }));

  return [{ value: '', name: 'Select duration' }, ...options];
};

const getDepositOptionLabel = (depositPercentage: number, price: number) => {
  const depositPrice = (depositPercentage / 100) * price;

  return `£${(depositPrice / 100).toFixed(2)} ${depositPercentage}%`;
};

export const getDepositOptions = (
  price: number,
  apr: string,
  rates: FinanceRate[],
): SelectOption[] => {
  const filteredRates = getRatesForApr(apr, rates);

  const minimumDepositPercentage =
    Math.min(...filteredRates.map((rate) => rate.min_deposit_pct || 0)) / 100;

  const maximumDepositPercentage =
    Math.max(...filteredRates.map((rate) => rate.max_deposit_pct || 0)) / 100;

  // Create an empty array with 0-10 in it
  const depositOptions = Array.from(Array(11).keys()).map(
    (value) => value * 10,
  );

  // TODO: Min deposit value

  const filteredDepositOptions = depositOptions.filter(
    (option) =>
      (minimumDepositPercentage === 0 || option >= minimumDepositPercentage) &&
      (maximumDepositPercentage === 0 || option <= maximumDepositPercentage),
  );

  return filteredDepositOptions.map((deposit) => ({
    name: getDepositOptionLabel(deposit, price),
    value: deposit.toString(),
  }));
};

const formatToPercentage = (apr?: number) => {
  if (!apr) return '';
  return `${apr / 100}`;
};

const getBnplRates = (rates: FinanceRate[] = []) =>
  rates.filter((rate) => rate.apr_type === 'BUYNOW_PAYLATER');

export const getDurationsForBnpl = (rates: FinanceRate[] = []): string[] => {
  const filteredRates = getBnplRates(rates);
  const durations = filteredRates.map(
    (rate) =>
      `${rate.deferred_duration} + ${rate.duration} + ${formatToPercentage(
        rate.apr,
      )}`,
  );

  const deDuplicatedDurations = durations
    .map((duration, index) => durations.indexOf(duration) === index && duration)
    .filter(Boolean) as string[];

  return deDuplicatedDurations.sort((a, b) => b.localeCompare(a));
};

export const mapBnplDurationsToSelectOptions = (
  durations: string[],
): SelectOption[] => {
  const options = durations.map((duration) => {
    const split = duration.split(' + ');
    return {
      value: duration,
      name: `${split[0]} months deferred, ${split[1]} months at ${split[2]}% APR`,
    };
  });
  return [{ value: '', name: 'Select duration' }, ...options];
};

const getBnplRate = (
  durationString: string,
  rates: FinanceRate[],
): FinanceRate | undefined => {
  const [deferred, duration, apr] = durationString.split(' + ');

  const parsedApr = Math.round(parseFloat(apr) * 100);
  const parsedDeferred = parseInt(deferred, 10);
  const parsedDuration = parseInt(duration, 10);

  const filtered = rates.filter(
    (rate: FinanceRate) =>
      rate?.apr === parsedApr &&
      rate?.deferred_duration === parsedDeferred &&
      rate?.duration === parsedDuration,
  )?.[0];

  if (!filtered) return undefined;
  return filtered;
};

export const getBnplDepositOptions = (
  price: number,
  duration: string,
  rates?: FinanceRate[],
): SelectOption[] => {
  if (!rates)
    return [
      {
        name: 'Select deposit',
        value: '',
      },
    ];

  const rate = getBnplRate(duration, rates);

  if (!rate)
    return [
      {
        name: 'Select deposit',
        value: '',
      },
    ];

  const minimumDepositPercentage =
    Math.min(rate?.min_deposit_pct || 0, 0) / 100;

  const maximumDepositPercentage =
    Math.max(rate?.max_deposit_pct || 0, 0) / 100;

  // Create an empty array with 0-10 in it
  const depositOptions = Array.from(Array(11).keys()).map(
    (value) => value * 10,
  );

  // TODO: Min deposit value

  const filteredDepositOptions = depositOptions.filter(
    (option) =>
      (minimumDepositPercentage === 0 || option >= minimumDepositPercentage) &&
      (maximumDepositPercentage === 0 || option <= maximumDepositPercentage),
  );

  return filteredDepositOptions.map((deposit) => ({
    name: getDepositOptionLabel(deposit, price),
    value: deposit.toString(),
  }));
};
